// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import { activeSkinName, skins } from '@/theme/skin';
import { writeStoredSkinName } from '@/theme/skinStorage';

/**
 * Picks the skin this device shows. The choice is stored and read at the next start; on web
 * the page reloads at once, on native the user restarts the app. Admin-only while skins are
 * being tried out, so the row lives behind the profile screen's admin gate.
 */
export default function SkinPicker() {
  const { t } = useI18n();
  const [chosen, setChosen] = useState(activeSkinName);
  const needsRestart = chosen !== activeSkinName;

  const choose = (name: string) => {
    writeStoredSkinName(name);
    setChosen(name);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <View>
      <View style={styles.row}>
        {Object.keys(skins).map((name) => {
          const selected = name === chosen;
          return (
            <Pressable
              key={name}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              testID={`skin-option-${name}`}
              onPress={() => choose(name)}
              style={[styles.pill, selected && styles.pillSelected]}
            >
              <Text style={[styles.pillText, selected && styles.pillTextSelected]}>{name}</Text>
            </Pressable>
          );
        })}
      </View>
      {needsRestart ? <Text style={styles.hint}>{t('profile.skinRestartHint')}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  pill: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.card,
  },
  pillSelected: {
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.accent.primary,
  },
  pillText: { color: appColors.text.subtle, fontWeight: '600' },
  pillTextSelected: { color: appColors.text.inverse },
  hint: { color: appColors.status.warningText, fontSize: 12, marginTop: 8 },
});
